import { Component, computed, inject, input } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { switchMap } from 'rxjs';

import { StatTileComponent } from '../../../shared/components/stat-tile/stat-tile.component';
import { MeterTileComponent } from '../../../shared/components/meter-tile/meter-tile.component';
import { ChartTileComponent } from './chart-tile.component';
import { ListTileComponent } from './list-tile.component';
import { OrgTileComponent } from './org-tile.component';
import { StatChartTileComponent } from './stat-chart-tile.component';
import { LeaveSummaryTileComponent } from './leave-summary-tile.component';
import { MultiStatTileComponent } from './multi-stat-tile.component';
import { DashboardDataRegistry } from './dashboard-data.registry';
import { DashboardPrefsService } from './dashboard-prefs.service';
import {
  WidgetDescriptor,
  WidgetRepresentation,
  loadingData,
} from './dashboard-widget.model';
import { AttendanceClockTileComponent } from './attendance-clock-tile.component';
import { UpcomingBirthdaysTileComponent } from "./upcoming-birthdays-title.component";

const CHART_TYPES: WidgetRepresentation[] = [
  'bar',
  'line',
  'donut',
  'pie',
  'radar',
];

@Component({
  selector: 'app-widget-host',
  standalone: true,
  imports: [
    StatTileComponent,
    MeterTileComponent,
    ChartTileComponent,
    ListTileComponent,
    OrgTileComponent,
    StatChartTileComponent,
    LeaveSummaryTileComponent,
    MultiStatTileComponent,
    AttendanceClockTileComponent,
    UpcomingBirthdaysTileComponent
],
  template: `
    @let d = data();
    @if (widget().id === 'attendance-clock') {
      <app-attendance-clock-tile
        [title]="widget().title"
        [subtitle]="widget().subtitle ?? ''"
        [icon]="widget().icon ?? ''"
        [accent]="widget().accent ?? ''"
      />
    } @else if (widget().id === 'upcoming-birthdays') {
      <app-upcoming-birthdays-tile
        [title]="widget().title"
        [subtitle]="widget().subtitle ?? ''"
        [icon]="widget().icon ?? ''"
        [accent]="widget().accent ?? ''"
        [birthdays]="d.birthdays ?? []"
        [loading]="d.loading"
      />
    } @else if (activeType() === 'multi-stat') {
      <app-multi-stat-tile [stats]="d.stats ?? []" [loading]="d.loading" />
    } @else if (activeType() === 'list') {
      <app-list-tile
        [title]="widget().title"
        [subtitle]="widget().subtitle ?? ''"
        [icon]="widget().icon ?? ''"
        [accent]="widget().accent ?? ''"
        [items]="d.items ?? []"
        [loading]="d.loading"
      />
    } @else if (activeType() === 'org') {
      <app-org-tile
        [title]="widget().title"
        [subtitle]="widget().subtitle ?? ''"
        [icon]="widget().icon ?? ''"
        [accent]="widget().accent ?? ''"
        [org]="d.org"
        [loading]="d.loading"
      />
    } @else if (activeType() === 'leave-summary') {
      <app-leave-summary-tile
        [title]="widget().title"
        [subtitle]="widget().subtitle ?? ''"
        [icon]="widget().icon ?? ''"
        [accent]="widget().accent ?? ''"
        [items]="d.leaveSummary ?? []"
        [link]="widget().link ?? '/leave/applications'"
        [reps]="widget().representation.options"
        (repChange)="onRepChange($event)"
        [loading]="d.loading"
      />
    } @else if (activeType() === 'stat-chart') {
      <app-stat-chart-tile
        [title]="widget().title"
        [value]="d.value"
        [max]="d.max"
        [unit]="d.unit ?? ''"
        [subtitle]="widget().subtitle ?? ''"
        [icon]="widget().icon ?? ''"
        [accent]="widget().accent ?? ''"
        [chart]="d.chart"
        [delta]="d.delta"
        [defaultStyle]="widget().chartStyle ?? 'bar'"
        [styleOptions]="widget().chartStyleOptions ?? []"
        [loading]="d.loading"
      />
    } @else if (isChart()) {
      <app-chart-tile
        [title]="widget().title"
        [subtitle]="widget().subtitle ?? ''"
        [icon]="widget().icon ?? ''"
        [accent]="widget().accent ?? ''"
        [badge]="chartBadge()"
        [type]="activeType()"
        [reps]="widget().representation.options"
        (repChange)="onRepChange($event)"
        [chart]="d.chart"
        [loading]="d.loading"
      />
    } @else if (activeType() === 'meter') {
      <app-meter-tile
        [value]="d.value"
        [max]="d.max"
        [unit]="d.unit ?? ''"
        [label]="widget().title"
        [icon]="widget().icon ?? ''"
        [accent]="widget().accent ?? ''"
        [link]="widget().link ?? '/'"
        [loading]="d.loading"
      />
    } @else {
      <!-- number, and the honest fallback for any type without a component. -->
      <app-stat-tile
        [value]="d.value"
        [unit]="d.unit ?? ''"
        [label]="widget().title"
        [icon]="widget().icon ?? ''"
        [accent]="widget().accent ?? ''"
        [link]="widget().link ?? '/'"
        [loading]="d.loading"
        [delta]="d.delta"
        [secondary]="d.secondary"
        [overlayItems]="widget().overlayItems"
      />
    }
  `,
})
export class WidgetHostComponent {
  private registry = inject(DashboardDataRegistry);
  private prefs = inject(DashboardPrefsService);

  widget = input.required<WidgetDescriptor>();

  /** Live data for this widget's source; loading tile until the first emit. */
  data = toSignal(
    toObservable(this.widget).pipe(
      switchMap((w) => this.registry.resolve(w.dataSourceId)),
    ),
    { initialValue: loadingData() },
  );

  /** User override wins over the descriptor's default representation. */
  activeType = computed<WidgetRepresentation>(() => {
    const w = this.widget();
    const override = this.prefs.reps()[w.id];
    // Guard a stale saved value against the widget's current option menu.
    if (override && w.representation.options.includes(override))
      return override;
    return w.representation.type;
  });

  isChart = computed(() => CHART_TYPES.includes(this.activeType()));

  onRepChange(rep: WidgetRepresentation): void {
    this.prefs.set(this.widget().id, rep);
  }

  /** A chart's summary value doubles as the header badge (e.g. "8.4% avg"). */
  chartBadge(): string {
    const v = this.data().value;
    return typeof v === 'string' ? v : '';
  }
}
